/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, reduxForm } from "redux-form";

import FormField from "components/FormField";
import FormSelector from "components/FormSelector";

import MDTypography from "components/MDTypography";
import FormDateTimePicker from "components/FormDateTimePicker";
import { getApartments, getApartmentsNames } from "../../clientReservationsUtils";

const validate = (values) => {
  const errors = {};
  const requiredFields = ["firstName", "lastName", "email"];

  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });

  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  return errors;
};

function ClientFrom(props) {
  const { handleSubmit, pristine, onSubmit, actionType, projects, apartments, initialValues } =
    props;

  const { t } = useTranslation();

  const apartmentOptions = getApartments(projects, initialValues?.projectId);
  const apartmentNames = getApartmentsNames(projects, initialValues?.projectId, apartments);

  const submitData = (data) => {
    onSubmit({
      ...data,
      startDate: undefined,
      endDate: undefined,
      projectId: undefined,
      apartmentId: undefined,
    });
  };

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(submitData)}>
        <Grid container justifyContent="center" alignItems="center" sx={{ overflow: "visible" }}>
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("client")}
            </MDBox>
            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="firstName" component={FormField} label={t("First Name")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="lastName" component={FormField} label={t("Last Name")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="email" component={FormField} label="Email" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="address" component={FormField} label={t("Address")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="city" component={FormField} label={t("City")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="postcode" component={FormField} label={t("Postcode")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="phoneNumber" component={FormField} label={t("Phone Number")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="clientLang"
                    component={FormSelector}
                    values={["es", "en"]}
                    names={[
                      { uid: "es", name: "Español" },
                      { uid: "en", name: "English" },
                    ]}
                    label={t("Language")}
                  />
                </Grid>
                {initialValues.startDate && (
                  <MDTypography color="error" variant="body2" sx={{ textAlign: "center" }}>
                    {t("oldClientMessage")}
                  </MDTypography>
                )}
                {initialValues.startDate && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="projectId"
                        values={Object.keys(projects || {})}
                        names={Object.values(projects || {})}
                        component={FormSelector}
                        label={t("project")}
                        disabled={actionType === "edit"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="apartmentId"
                        values={apartmentOptions}
                        names={apartmentNames}
                        component={FormSelector}
                        label={t("apartment")}
                        disabled={actionType === "edit"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="startDate"
                        component={FormDateTimePicker}
                        label={t("startDate")}
                        disabled={actionType === "edit"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field
                        name="endDate"
                        component={FormDateTimePicker}
                        label={t("endDate")}
                        disabled={actionType === "edit"}
                      />
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" alignItems="center" flexDirection="column" mt={2} xs={12}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={pristine && initialValues.startDate === undefined}
                    >
                      {t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ClientFrom.defaultProps = {
  projects: {},
  apartments: {},
  initialValues: {},
};

ClientFrom.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  initialValues: PropTypes.object,
};

const mapStateToProps = (state, props) => {
  const { actionType, uids } = props;

  let userDataToEdit = {};

  const { clientsReducer, projectsReducer, apartmentsReducer } = state;

  if (actionType === "edit" && uids.length === 1) {
    if (Object.prototype.hasOwnProperty.call(clientsReducer, uids[0]))
      userDataToEdit = clientsReducer[uids[0]];
  }

  if (actionType !== "edit" && !userDataToEdit.clientLang) {
    userDataToEdit.clientLang = "es";
  }

  return {
    initialValues: userDataToEdit,
    projects: projectsReducer,
    apartments: apartmentsReducer,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "ClientFrom", // a unique identifier for this form
    validate,
  })(ClientFrom)
);
